import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { PhotoCarouselDot } from './photo-carousel-dot';
import { PhotoCarouselDots } from './photo-carousel-dots';
import { PhotoCarouselNavControls } from './photo-carousel-nav-controls';
import { PhotoCarouselNextButton } from './photo-carousel-next-button';
import { PhotoCarouselPreviousButton } from './photo-carousel-previous-button';
import { PhotoCarouselRoot } from './photo-carousel-root';
import { PhotoCarouselSlide } from './photo-carousel-slide';
import { PhotoCarouselSlides } from './photo-carousel-slides';
import type { PhotoCarouselPropsInterface } from './types/photo-carousel.types';

/**
 * Displays a carousel of photos inside of a `PropertyCard`.
 *
 * To change slides, mouse users can use navigation buttons, which appear on hover.
 * Keyboard users can tab to focus on the carousel dots and then use the left and right
 * arrow keys. Touchscreen users can swipe.
 *
 * While this component was created specifically for `PropertyCard`, it is exported
 * separately so that consumers don't have to import it if they don't need it.
 *
 * @related ['Carousel', 'PropertyCard']
 * @since 8.66.0
 */

export const PhotoCarousel = forwardRef<HTMLDivElement, PhotoCarouselPropsInterface>(
  (props: PhotoCarouselPropsInterface, forwardedRef) => {
    const { children, maxDotsToDisplay, renderDot, renderSlide, ...rest } = props;

    return (
      <PhotoCarousel.Root ref={forwardedRef} {...rest}>
        <PhotoCarousel.Dots maxDotsToDisplay={maxDotsToDisplay} renderDot={renderDot} />
        <PhotoCarousel.NavControls>
          <PhotoCarousel.PreviousButton />
          <PhotoCarousel.NextButton />
        </PhotoCarousel.NavControls>
        <PhotoCarousel.Slides renderSlide={renderSlide}>{children}</PhotoCarousel.Slides>
      </PhotoCarousel.Root>
    );
  },
) as ForwardRefExoticComponent<PhotoCarouselPropsInterface> & {
  Root: typeof PhotoCarouselRoot;
  Dot: typeof PhotoCarouselDot;
  Dots: typeof PhotoCarouselDots;
  NavControls: typeof PhotoCarouselNavControls;
  NextButton: typeof PhotoCarouselNextButton;
  PreviousButton: typeof PhotoCarouselPreviousButton;
  Slide: typeof PhotoCarouselSlide;
  Slides: typeof PhotoCarouselSlides;
};

PhotoCarousel.displayName = 'PhotoCarousel';

PhotoCarousel.Root = PhotoCarouselRoot;
PhotoCarousel.Dot = PhotoCarouselDot;
PhotoCarousel.Dots = PhotoCarouselDots;
PhotoCarousel.NavControls = PhotoCarouselNavControls;
PhotoCarousel.NextButton = PhotoCarouselNextButton;
PhotoCarousel.PreviousButton = PhotoCarouselPreviousButton;
PhotoCarousel.Slide = PhotoCarouselSlide;
PhotoCarousel.Slides = PhotoCarouselSlides;
